import { Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { SubStepEnum } from '../models/sub-step.enum';
import { Decoration } from '../../../../../models/configurators-2d/applications/fence/decoration';
import { MaterialsFacadeService } from '../services/materials.facade.service';
import { MaterialSubStepService } from '../services/material-sub-step.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Configuration } from '../../../../../models/configurators-2d/applications/fence/configuration';
import { TranslateService } from '@ngx-translate/core';
import { FenceService } from '../../../../../services/configurators-2d/applications/fence.service';
import { getMaxHeight } from '../../../../../services/helpers/fence-helper';
import { Side } from '../../../../../models/configurators-2d/applications/fence/side';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { DecorationStateService } from '../../services/decoration.state.service';
import { InstallationTypeEnum } from 'src/app/enum/fence/installation-type.enum';

@Component({
  selector: 'ng-casto-materials-decors',
  templateUrl: './materials-decors.component.html',
  styleUrl: './materials-decors.component.scss',
})
export class MaterialsDecorsComponent implements OnInit {
  /** Base URL de l'API */
  baseImgURL: string = undefined;

  /** Langue sélectionnée */
  currentLang: string;

  protected readonly SubStepEnum = SubStepEnum;
  protected readonly InstallationTypeEnum = InstallationTypeEnum;
  protected readonly getMaxHeight = getMaxHeight;
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly materialSubStepService = inject(MaterialSubStepService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly fenceService = inject(FenceService);
  private readonly decorationStateService = inject(DecorationStateService);

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  /**
   * Retourne le message de déréférencement
   */
  protected get disableMessage(): string {
    return this.materialsFacadeService.disableMessage();
  }

  /**
   * Retourne la configuration actuelle
   */
  protected get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  /**
   * Retourne la liste des décorations disponibles
   */
  protected get decorations(): Decoration[] {
    return this.decorationStateService.decors();
  }

  /**
   * Retourne la sous-étape courante du composant
   * @protected
   */
  protected get subStep(): SubStepEnum {
    return this.materialSubStepService.subStep();
  }

  /**
   * Vérifie si la liste de décorations contient des éléments désactivés
   * @returns true si au moins une décoration est désactivée, false sinon
   */
  protected get isDecorationsValid(): boolean {
    return this.materialsFacadeService.isDecorationsValid();
  }

  /**
   * Est-ce que la liste des décors contient des éléments pouvant être placés horizontalement ?
   */
  hasHorizontalDecorations = computed(() => {
    return this.decorations.some(x => x.canBeHorizontal && !this.configuration.isVertical);
  });

  /**
   * Est-ce que la liste des décors contient des éléments pouvant être placés verticalement ?
   */
  hasVerticalDecorations = computed(() => {
    return this.decorations.some(x => x.canBeVertical && getMaxHeight(this.configuration) >= x.width);
  });

  /**
   * Est-ce que la liste des décors contient des éléments pouvant être placés horizontalement ou verticalement ?
   */
  hasDecorations = computed(() => {
    return this.hasHorizontalDecorations() || this.hasVerticalDecorations();
  });

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Gère l'affichage/masquage des sous-menus en accordéon
   * @param step - Numéro du sous-menu (1: Lames, 2: Poteaux, 3: Lames décoratives, 4: Décors, 5: Panneaux, 6: Portillons)
   */
  protected toggleCollapse(step: SubStepEnum): void {
    this.materialSubStepService.toggleCollapse(step);
  }

  /**
   * Supprime toutes les décorations de la configuration
   */
  protected deleteAllDecorations(): void {
    this.materialsFacadeService
      .deleteAllDecorations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur sélection postType:', err),
      });
  }

  /**
   * Ajoute ou retire une décoration de la configuration
   * @param type - Type de décoration ('horizontal' ou 'vertical')
   * @param decoration - La décoration à ajouter/retirer
   */
  protected addDecoration(type: 'horizontal' | 'vertical', decoration: Decoration): void {
    this.materialsFacadeService
      .addDecoration(type, decoration)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur ajout decoration:', err),
      });
  }

  /**
   * Vérifie si une décoration est cochée dans la configuration
   * @param type - Type de décoration ('horizontal' ou 'vertical')
   * @param decoration - Décoration
   * @returns true si la décoration est cochée, false sinon
   */
  protected isDecorationChecked(type: 'horizontal' | 'vertical', decoration: Decoration): boolean {
    return this.materialsFacadeService.isDecorationChecked(type, decoration);
  }

  /**
   * Modifie la quantité d'une décoration
   * @param type - Type de décoration ('horizontal' ou 'vertical')
   * @param decoration - La décoration concernée
   * @param sideId - Identifiant du côté (pour les décorations verticales), null pour les horizontales
   * @param event - L'événement de changement
   */
  protected changeDecorationQuantity(
    type: 'horizontal' | 'vertical',
    decoration: Decoration,
    sideId: string | null,
    event: Event
  ): void {
    this.materialsFacadeService
      .changeDecorationQuantity(type, decoration, sideId, event)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement quantité decoration:', err),
      });
  }

  /**
   * Calcule le nombre maximum d'éléments horizontaux (lames décoratives/décorations/panneaux) autorisés
   * Prend en compte le nombre de panneaux complets et les éléments déjà placés
   * @param element - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @returns Le nombre maximum d'éléments horizontaux autorisés
   */
  protected maxHorizontalElement(element: Decoration): number {
    return this.materialsFacadeService.maxHorizontalElement(element);
  }

  /**
   * Récupère la quantité d'une décoration dans la configuration
   * @param type - Type de décoration ('horizontal' ou 'vertical')
   * @param decoration - Décoration
   * @param side - Côté (pour les décorations verticales), null pour les horizontales
   * @returns La quantité de la décoration
   */
  protected getDecorationQuantity(type: 'horizontal' | 'vertical', decoration: Decoration, side: Side | null): number {
    return this.materialsFacadeService.getDecorationQuantity(type, decoration, side);
  }

  /**
   * Calcule le nombre maximum d'éléments verticaux (lames décoratives/décorations/panneaux) autorisés pour un côté
   * Prend en compte la longueur du côté et les éléments déjà placés
   * @param element - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @param side - Le côté de la clôture
   * @returns Le nombre maximum d'éléments verticaux autorisés pour ce côté
   */
  protected maxVerticalElement(element: Decoration, side: Side): number {
    return this.materialsFacadeService.maxVerticalElement(element, side);
  }
}
